import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Button from 'srcReact/components/common2/Button'
import { TextBox } from 'srcReact/components/common2/TextBox'
import styles from './styles.module.scss'
import Icon from 'srcReact/components/icons/Icon'
import { useDispatch, useSelector } from 'react-redux'
import { commonActions } from 'srcReact/components/actions'
import IconButton from 'srcReact/components/common2/IconButton'
import { organizationSettingsActions } from 'srcReact/OrganizationSettingsPage/actions'
import Tabs from 'srcReact/components/common2/Tabs'
import SuccessMessage from 'srcReact/components/common2/SuccessMessage'
import { isEmpty, isValidLink } from 'srcReact/helpers/validator'
import ErrorMessage from 'srcReact/components/common2/ErrorMessage'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import Spinner from 'srcReact/components/common2/Spinner'

function UploadDocumentModal({ orgId, fileToUpdate, isLink, closeModal }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const fileInputRef = useRef(null)
  const [scope, setScope] = useState('device')
  const [validationError, setValidationError] = useState('')
  const [acceptedDocTypes, setAcceptedDocTypes] = useState([])
  const [confirmClicked, setConfirmClicked] = useState(false)
  const [file, setFile] = useState(fileToUpdate || null)
  const [fileUrl, setFileUrl] = useState({
    name: 'docLink',
    value: '',
    hasError: false,
    errorMessage: '',
    isRequired: false
  })

  const {
    common: {
      fileCreation: { files: uploadedFiles, loadingFile, error: fileUploadError }
    },
    organizationSettings: {
      orgFiles: { loadingAddUpdateFile, addUpdateFileError, success }
    }
  } = useSelector((state) => state)

  useEffect(() => {
    const acceptedDocs =
      appConstantSettings.ACCEPTED_DOCUMENT_TYPES.length > 0 &&
      appConstantSettings.ACCEPTED_DOCUMENT_TYPES.split(',')
        .map((type) => `.${type}`)
        .join(', ')
    setAcceptedDocTypes(acceptedDocs)
    setScope(isLink ? 'link' : 'device')
    if (fileToUpdate && isLink) {
      setFileUrl({
        ...fileUrl,
        value: fileToUpdate.file_url
      })
    }
    return () => {
      dispatch(organizationSettingsActions.clearFlagsForOrgDocs())
    }
  }, [])

  useEffect(() => {
    if (!loadingAddUpdateFile && success) {
      setTimeout(() => {
        closeModal()
      }, 2000)
    }
  }, [loadingAddUpdateFile, success])

  useEffect(() => {
    if (uploadedFiles?.length > 0 && !loadingFile && confirmClicked) {
      addUpdateOrgFile()
      setConfirmClicked(false)
    }
  }, [uploadedFiles, loadingFile, confirmClicked])

  const handleLinkChange = (e) => {
    setFileUrl({
      ...fileUrl,
      value: e.target.value
    })
  }

  const openFilePicker = () => {
    if (fileInputRef) {
      fileInputRef.current.click()
    }
  }
  const selectFile = (e) => {
    setValidationError('')
    const files = e.target.files
    if (files?.length > 0) {
      setFile(files[0])
    }
  }

  const validateFile = (file) => {
    if (!file) {
      setValidationError(t('global.uploadDoc'))
      return false
    } else if (file) {
      const fileSizeInMB = file.size / 1024 / 1024 // in MiB

      if (fileSizeInMB > appConstantSettings.MAX_DOC_SIZE_MB) {
        setValidationError(
          t('messenger.clientErrors.docFileSizeError', { docFileSize: appConstantSettings.MAX_DOC_SIZE_MB })
        )
        return false
      }
      return true
    }
    return true
  }

  const validateLink = (link) => {
    if (isEmpty(link)) {
      setValidationError(t('event.create.clientErrors.docLinkRequired'))
      return false
    } else if (!isValidLink(link)) {
      setValidationError(t('event.create.clientErrors.invalidLink'))
      return false
    }
    return true
  }

  const addUpdateOrgFile = () => {
    if (scope === 'device') {
      if (fileToUpdate) {
        dispatch(organizationSettingsActions.updateOrgFile(orgId, fileToUpdate?.id, uploadedFiles[0].id))
      } else {
        dispatch(organizationSettingsActions.addOrgFile(orgId, uploadedFiles[0].id))
      }
    } else {
      let url = fileUrl.value
      if (scope === 'link' && !fileUrl?.value?.match(/^(https?)/)) {
        url = `http://${url}`
      }
      if (fileToUpdate) {
        dispatch(organizationSettingsActions.updateOrgFile(orgId, fileToUpdate.id, null, url))
      } else {
        dispatch(organizationSettingsActions.addOrgFile(orgId, null, url))
      }
    }
  }

  const handleConfirm = () => {
    if (scope === 'device') {
      if (!validateFile(file)) {
        return
      }
      setConfirmClicked(true)
      dispatch(
        commonActions.fileCreation([file], {
          type: 'document',
          is_logged: 1,
          files_count: 1
        })
      )
    } else {
      if (!validateLink(fileUrl.value)) {
        return false
      }
      setConfirmClicked(true)
      addUpdateOrgFile()
    }
  }

  const removeUploadedFile = () => {
    setFile(null)
  }

  const changeScope = (scope) => {
    setValidationError('')
    setScope(scope)
  }
  return (
    <div className={styles.uploadWrapper}>
      <Tabs type='boxed' initialActive={isLink ? 1 : 0} className={styles.tabs}>
        <div label={t('organizationSettings.documents.fromDevice')} onClick={() => changeScope('device')}>
          <div className={styles.upload}>
            <h2 className={styles.title}>{t('organizationSettings.documents.fromDevice')}</h2>
            <Icon name='device' styleModifier={styles.deviceIcon} size='xlarge' />
            {file ? (
              <div className={styles.uploadedFile}>
                <IconButton iconName='close24' iconSize='large' onClick={removeUploadedFile} />
                <span>{file?.name || file?.file_info?.name}</span>
              </div>
            ) : (
              <React.Fragment>
                <input
                  accept={acceptedDocTypes}
                  ref={fileInputRef}
                  className={styles.inputFile}
                  type='file'
                  id='upload-doc'
                  onChange={selectFile}
                />
                <button type='button' onClick={openFilePicker}>
                  {t('global.upload')}
                </button>
              </React.Fragment>
            )}
            {validationError && <ErrorMessage text={validationError} />}
          </div>
        </div>

        <div label={t('organizationSettings.documents.fromLink')} onClick={() => changeScope('link')}>
          <div className={styles.upload}>
            <h2 className={styles.title}>{t('organizationSettings.documents.fromLink')}</h2>
            <Icon name='link' size='xlarge' />
            <TextBox
              isRequired={false}
              onChange={handleLinkChange}
              placeholder={t('organizationSettings.documents.copyhere')}
              fieldInfo={fileUrl}
              customWrapperCss={styles.inputText}
            />
            {(validationError || addUpdateFileError) && <ErrorMessage text={validationError || addUpdateFileError} />}
          </div>
        </div>
      </Tabs>

      {(loadingAddUpdateFile || loadingFile) && (
        <div className={styles.spinnerWrapper}>
          <Spinner customCss={styles.spinner} />
          {t('global.uploadingDocument')}
        </div>
      )}

      <div className={styles.buttons}>
        <Button style='secondary' text={t('global.cancel')} handleClick={closeModal} />
        <Button type='button' text={t('global.confirm')} disabled={success} handleClick={handleConfirm} />
      </div>

      {success && (
        <SuccessMessage
          className={styles.success}
          text={
            scope === 'device'
              ? fileToUpdate
                ? t('organizationSettings.documents.successUpdate', { type: t('global.document') })
                : t('organizationSettings.documents.successUpload')
              : fileToUpdate
              ? t('organizationSettings.documents.successUpdate', { type: t('global.link') })
              : t('organizationSettings.documents.successLink')
          }
        />
      )}
    </div>
  )
}

export default UploadDocumentModal
